import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaTypeEnum, permissions } from '@ooh/contracts';
import type { Role, PermissionGrouped } from '@ooh/contracts';
import { useSuspenseQueries } from '@tanstack/react-query';
import { apiQueryOptions } from '../../data/use-api-suspense-query';
import { useApiClient } from '../../plugs/api-client';
import type { MediaTypeOption } from './grant-row';

export interface RoleCatalog {
  roles: Role[];
  groupedPermissions: PermissionGrouped;
  resourceOrder: string[];
  sectionLabels: Record<string, string>;
  mediaTypeOptions: MediaTypeOption[];
}

/**
 * Reads the role list and the full permission catalog, and derives the
 * resource-section labels and media-type option list the grant editor needs.
 */
export function useRoleCatalog(): RoleCatalog {
  const { t } = useTranslation('admin');
  const { t: tMap } = useTranslation('map');

  // includeInactive: true — the permission-matrix admin screen must still
  // show (and allow reactivating) a deactivated role, not make it disappear.
  const api = useApiClient();
  const [{ data: roles }, { data: groupedPermissions }] = useSuspenseQueries({
    queries: [
      apiQueryOptions(api, permissions.getRoles, { includeInactive: true }),
      apiQueryOptions(api, permissions.getAll, {}),
    ],
  });

  const resourceOrder = useMemo(() => Object.keys(groupedPermissions), [groupedPermissions]);
  const sectionLabels = useMemo(
    () =>
      Object.fromEntries(
        resourceOrder.map((resource) => [
          resource,
          t(`permissionMatrix.sections.${resource}`, { defaultValue: resource }),
        ]),
      ),
    [resourceOrder, t],
  );
  const mediaTypeOptions: MediaTypeOption[] = useMemo(
    () => MediaTypeEnum.options.map((code) => ({ code, label: tMap(`legend.types.${code}`) })),
    [tMap],
  );

  return { roles, groupedPermissions, resourceOrder, sectionLabels, mediaTypeOptions };
}
